import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", code: "", monthly_price: 0, yearly_price: 0, currency: "INR", trial_days: 14,
                  max_users: 0, max_orders: 0, features: [], description: "", active: true };

function PlanList() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [featuresStr, setFeaturesStr] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/plans");
    setPlans(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY }); setFeaturesStr(""); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setFeaturesStr((p.features || []).join("\n")); setOpen(true); };

  const save = async () => {
    const payload = { ...form, features: featuresStr.split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/admin/plans/${editing.id}`, payload);
      else await api.post("/admin/plans", payload);
      toast.success(editing ? "Plan updated" : "Plan created");
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete plan ${p.name}?`)) return;
    await api.delete(`/admin/plans/${p.id}`); load();
  };

  return (
    <div className="space-y-4" data-testid="plans-list">
      <div className="flex justify-end">
        <Button onClick={openCreate} data-testid="create-plan-btn"><Plus className="w-4 h-4 mr-1" /> New plan</Button>
      </div>
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Code</TableHead><TableHead>Monthly</TableHead><TableHead>Yearly</TableHead>
              <TableHead>Trial</TableHead><TableHead>Features</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plans.map((p) => (
              <TableRow key={p.id} data-testid={`plan-row-${p.code}`}>
                <TableCell className="font-semibold">{p.name}</TableCell>
                <TableCell className="font-mono text-xs">{p.code}</TableCell>
                <TableCell>₹{(p.monthly_price || 0).toLocaleString()}</TableCell>
                <TableCell>₹{(p.yearly_price || 0).toLocaleString()}</TableCell>
                <TableCell>{p.trial_days}d</TableCell>
                <TableCell className="text-xs">{(p.features || []).length} feats</TableCell>
                <TableCell>{p.active ? <Badge>Active</Badge> : <Badge variant="outline">Off</Badge>}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-${p.code}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(p)} data-testid={`del-${p.code}`}><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {plans.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No plans yet — create one to start charging.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit plan" : "New plan"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="plan-name" /></div>
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!!editing} data-testid="plan-code" /></div>
            <div><Label>Monthly price (₹)</Label><Input type="number" value={form.monthly_price} onChange={(e) => setForm({ ...form, monthly_price: parseFloat(e.target.value || "0") })} data-testid="plan-monthly" /></div>
            <div><Label>Yearly price (₹)</Label><Input type="number" value={form.yearly_price} onChange={(e) => setForm({ ...form, yearly_price: parseFloat(e.target.value || "0") })} data-testid="plan-yearly" /></div>
            <div><Label>Trial days</Label><Input type="number" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: parseInt(e.target.value || "0", 10) })} data-testid="plan-trial" /></div>
            <div><Label>Max users (0 = unlimited)</Label><Input type="number" value={form.max_users} onChange={(e) => setForm({ ...form, max_users: parseInt(e.target.value || "0", 10) })} /></div>
            <div className="col-span-2"><Label>Description</Label><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>Features (one per line)</Label>
              <textarea className="w-full border rounded p-2 text-sm" rows="4" value={featuresStr} onChange={(e) => setFeaturesStr(e.target.value)} data-testid="plan-features" />
            </div>
            <div className="col-span-2 flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> <Label>Active</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-plan">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MockedTable({ title }) {
  return (
    <div className="surface-card p-8 text-center" data-testid={`mocked-${title.toLowerCase()}`}>
      <h3 className="font-display text-xl font-bold mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm">Payment gateway integration is <Badge className="mx-1">MOCKED</Badge>. Wire a real provider (Stripe / Razorpay / PayPal) from <span className="font-mono text-xs">Integrations → Payment gateways</span> and entries will populate here automatically.</p>
    </div>
  );
}

export default function Subscriptions() {
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-subscriptions">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Subscriptions</h1>
        <p className="text-muted-foreground">Pricing plans, billing cycles, coupons, renewals, invoices and refunds.</p>
      </div>
      <Tabs defaultValue="plans" className="space-y-4">
        <TabsList>
          <TabsTrigger value="plans" data-testid="tab-plans">Plans</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="refunds" data-testid="tab-refunds">Refunds</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="tab-coupons">Coupons</TabsTrigger>
          <TabsTrigger value="renewals" data-testid="tab-renewals">Renewals</TabsTrigger>
        </TabsList>
        <TabsContent value="plans"><PlanList /></TabsContent>
        <TabsContent value="invoices"><MockedTable title="Invoices" /></TabsContent>
        <TabsContent value="payments"><MockedTable title="Payments" /></TabsContent>
        <TabsContent value="refunds"><MockedTable title="Refund requests" /></TabsContent>
        <TabsContent value="coupons"><MockedTable title="Coupons" /></TabsContent>
        <TabsContent value="renewals"><MockedTable title="Renewal queue" /></TabsContent>
      </Tabs>
    </div>
  );
}
